export const WHATSAPP_NUMBER = '17866035862'

export type ProfileType = 'embrionaria' | 'parcial' | 'solida'

export interface Option {
  text: string
  value: number
}

export interface Point {
  id: number
  title: string
  body: string[]
  evaluationQuestion: string
  options: Option[]
}

export const points: Point[] = [
  {
    id: 1,
    title: 'Procesos clave documentados',
    body: [
      'Los procesos críticos de tu negocio — cómo se atiende a un cliente, cómo se cotiza, cómo se entrega, cómo se cobra — ¿viven en algún sistema accesible para tu equipo, o viven en tu cabeza?',
      "Cuando los procesos viven en la cabeza del dueño, el negocio funciona solo mientras el dueño está presente. Cada nuevo integrante del equipo aprende observando, preguntando, equivocándose. Cada proceso depende de que alguien recuerde 'cómo es que se hacía esto'.",
      'Un proceso documentado no es burocrático. Es la diferencia entre un equipo que ejecuta con autonomía y un equipo que necesita supervisión constante.',
      'La estructura operativa empieza aquí: lo que se hace tiene que poder explicarse, leerse y replicarse sin tu intervención.',
    ],
    evaluationQuestion:
      '¿Los procesos clave de tu negocio están documentados y accesibles para tu equipo?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 2,
    title: 'Roles y responsabilidades claras',
    body: [
      'Cada persona en tu negocio debería poder responder con claridad a tres preguntas: ¿qué hago yo aquí, qué decisiones puedo tomar sin consultar, y qué espera mi rol que entregue?',
      "Cuando los roles están difusos, todo termina pasando por el dueño. No por mala voluntad del equipo — por falta de claridad. Si nadie sabe exactamente hasta dónde llega su responsabilidad, todo lo que esté en el límite se convierte en algo que 'mejor pregunto al jefe'.",
      "La estructura operativa exige roles definidos. No descripciones genéricas tipo 'gerente de operaciones', sino claridad concreta sobre qué decisiones, qué procesos y qué resultados son de cada quien.",
      'Cuando esa claridad existe, la operación deja de depender de tu presencia. Y tú dejas de ser el cuello de botella.',
    ],
    evaluationQuestion:
      '¿Tu equipo tiene claridad sobre qué decisiones pueden tomar sin consultarte?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 3,
    title: 'Criterios de decisión definidos',
    body: [
      'En tu negocio se toman decenas de decisiones cada semana. ¿Cuáles de esas decisiones se toman aplicando criterios establecidos, y cuáles dependen de la intuición o experiencia individual de quien le toca decidir?',
      'Una operación con estructura tiene criterios definidos para las decisiones recurrentes: cómo se prioriza un cliente, cómo se aprueba un descuento, cómo se evalúa una contratación, cómo se define qué proyecto sigue y cuál se pausa.',
      'Sin criterios, cada decisión es un evento individual que consume energía y genera inconsistencia. Hoy se aprueba un descuento, mañana no, porque depende del estado de ánimo o del cliente que pidió.',
      'Los criterios convierten decisiones complejas en aplicaciones de un sistema. Eso libera tiempo, da consistencia y permite delegar.',
    ],
    evaluationQuestion:
      '¿Las decisiones recurrentes de tu negocio tienen criterios definidos que cualquiera del equipo puede aplicar?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 4,
    title: 'Métricas que importan, medidas con disciplina',
    body: [
      'Tu negocio tiene cientos de números posibles que medir. Pero hay 3-5 que realmente determinan si la operación está saludable. ¿Sabes cuáles son esos para tu negocio? ¿Y los revisas con regularidad?',
      'Una operación sin métricas claras es como manejar de noche sin luces. Vas avanzando, pero no sabes si te acercas o te alejas del destino. Las decisiones se toman por sensación.',
      'La estructura operativa requiere identificar qué números reflejan la salud real del negocio — no todos, los que importan — y construir disciplina para revisarlos. Semanal, mensual, trimestral, según cuál sea cada uno.',
      'Cuando esas métricas existen y se revisan, las conversaciones del equipo cambian. Dejan de ser opiniones y empiezan a ser análisis.',
    ],
    evaluationQuestion:
      '¿Tienes definidos y revisas regularmente los 3-5 indicadores críticos que reflejan la salud real de tu negocio?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 5,
    title: 'Reuniones con propósito y resultado',
    body: [
      'Las reuniones consumen las horas más caras de un negocio: las del dueño y las del equipo de liderazgo. ¿Tus reuniones tienen estructura — agenda clara, decisiones documentadas, próximos pasos asignados — o son conversaciones abiertas que terminan sin resolver nada concreto?',
      'Cuando las reuniones no están estructuradas, se convierten en el síntoma más caro de la falta de estructura general del negocio: tiempo gastado, decisiones diferidas, mismo problema discutido tres veces sin avanzar.',
      'Una operación sólida tiene cadencia de reuniones definida (cuándo, con quién, para qué) y formato repetible que asegura que cada reunión produce decisiones claras, no solo información.',
      'Esto no es burocracia. Es respeto por el tiempo más valioso del negocio.',
    ],
    evaluationQuestion:
      '¿Tus reuniones tienen agenda, producen decisiones claras y dejan próximos pasos asignados con responsable?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 6,
    title: 'Capacidad real de delegar',
    body: [
      "Cuando intentas delegar una responsabilidad importante en tu equipo, ¿la persona puede asumirla con autonomía, o terminas retomándola porque 'no quedó como tú la harías'?",
      'La incapacidad de delegar bien rara vez es problema de las personas. Es problema de estructura. Si los procesos no están documentados, los criterios no están claros, y las métricas no están definidas — delegar se vuelve un acto de fe.',
      'La estructura operativa hace posible la delegación real. No es soltar y rezar. Es transferir la responsabilidad junto con la información, los criterios y las herramientas que la hacen ejecutable.',
      'Cuando esa estructura existe, delegar deja de ser un acto de coraje y se vuelve la consecuencia natural de un sistema bien diseñado.',
    ],
    evaluationQuestion:
      '¿Cuando delegas algo importante, tu equipo puede ejecutarlo con autonomía sin que termines retomándolo?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 7,
    title: 'Independencia operativa del dueño',
    body: [
      'Si te tomaras dos semanas completamente desconectado — sin revisar mensajes, sin tomar decisiones, sin intervenir en nada — ¿cómo estaría tu negocio cuando regreses?',
      'Esta es la prueba final de la estructura operativa. No es una pregunta hipotética: es la radiografía más honesta del estado actual de tu negocio.',
      'Si el negocio funciona sin ti durante dos semanas, tienes estructura real. Si funciona a medias, tienes estructura parcial. Si no funciona o se rompe, lo que tienes no es exactamente un negocio: es una operación atada a tu presencia.',
      'No hay punto intermedio en esta evaluación. Y la respuesta determina qué tan urgente es construir lo que falta.',
    ],
    evaluationQuestion:
      'Si te desconectas dos semanas completas del negocio, ¿el negocio funciona normal en tu ausencia?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 8,
    title: 'Sistema de mejora continua',
    body: [
      'Una estructura operativa no es estática. Los procesos que funcionan hoy no necesariamente van a funcionar dentro de 12 meses, cuando el negocio crezca o el contexto cambie.',
      '¿Tu negocio tiene un mecanismo claro para detectar qué procesos necesitan ajustarse, qué criterios están desactualizados, qué partes de la operación están empezando a generar fricción?',
      'Sin un sistema de mejora continua, la estructura que construyes hoy se vuelve la estructura que te limita mañana. La operación se rigidiza. Los procesos siguen aplicándose aunque ya no tengan sentido.',
      'La estructura operativa madura no solo opera bien — se cuestiona y se actualiza periódicamente. Esa es la diferencia entre un sistema que escala y uno que colapsa cuando el negocio crece.',
    ],
    evaluationQuestion:
      '¿Tienes algún mecanismo para revisar y ajustar periódicamente los procesos, criterios y reglas de tu operación?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
]

export const profiles = {
  embrionaria: {
    name: 'Estructura embrionaria',
    analysis: [
      'Tu respuesta a los 8 puntos indica que tu negocio depende casi por completo de tu presencia para funcionar. Esto es común — más común de lo que la mayoría admite — y no es una sentencia. Es un punto de partida.',
      'Lo importante de este nivel: la dependencia del dueño se siente como un techo. Más esfuerzo no escala. Más herramientas tampoco. Lo que necesita el negocio es construir las primeras piezas de estructura — procesos, criterios, roles — que permitan a la operación funcionar sin tu intervención constante.',
      'Este es el momento donde el trabajo de estructura genera el mayor retorno por hora invertida. Cada proceso documentado, cada criterio definido, cada rol clarificado, libera tiempo de manera exponencial.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu checklist de estructura y salí en 'Estructura embrionaria'. Quiero revisar mi caso específico contigo.",
  },
  parcial: {
    name: 'Estructura parcial',
    analysis: [
      'Tu respuesta indica que tu negocio tiene piezas de estructura ya construidas — algunos procesos documentados, ciertos roles claros, métricas que se miran — pero también hay zonas donde el negocio aún te necesita demasiado.',
      'Este es el punto donde la mayoría se estanca. Las cosas funcionan a medias y no hay urgencia visible. Algunos procesos están definidos, otros viven en tu cabeza. Algunas decisiones tienen criterios, otras dependen de quién las tome.',
      'El siguiente nivel no requiere construir todo desde cero. Requiere identificar las 2-3 zonas más críticas donde la falta de estructura sigue costando — en tiempo tuyo, en errores del equipo, en decisiones que no se toman bien — y resolverlas con intención.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu checklist de estructura y salí en 'Estructura parcial'. Quiero revisar mi caso específico contigo.",
  },
  solida: {
    name: 'Estructura sólida',
    analysis: [
      'Tu respuesta indica que tu operación tiene cimientos reales. Procesos documentados, roles claros, criterios definidos, métricas medidas. Esto te pone en una posición que la mayoría de los dueños no alcanza.',
      'En este nivel, la pregunta ya no es si tienes estructura — es si la estructura actual está diseñada para lo que viene. El próximo nivel del negocio (más volumen, más equipo, más complejidad, más geografías) puede romper estructuras que hoy funcionan perfectamente.',
      'El trabajo aquí es distinto: revisar qué piezas de la estructura actual fueron diseñadas para una etapa anterior y necesitan evolucionar. Construir desde la calma es ventaja sobre construir bajo presión cuando el crecimiento ya está exigiendo.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu checklist de estructura y salí en 'Estructura sólida'. Quiero revisar mi caso específico contigo.",
  },
}

export const pointAnalysis: Record<number, string> = {
  1: 'Tus procesos clave viven principalmente en la cabeza del equipo, no en el sistema. Cada nuevo integrante aprende observando, cada decisión depende de que alguien recuerde cómo se hacía. El primer movimiento aquí es identificar los 3 procesos más críticos del negocio y documentarlos. Eso solo, sin tocar nada más, libera horas tuyas en las próximas semanas.',
  2: 'Los roles del equipo están difusos. Eso hace que decisiones que deberían resolverse en otros niveles terminen llegando a ti. El trabajo concreto: definir para los 3-5 roles más importantes qué decisiones puede tomar cada quien sin consultarte. No descripciones de cargo genéricas — claridad sobre alcance de decisión.',
  3: 'Las decisiones recurrentes de tu negocio se toman caso por caso, sin un criterio establecido. Eso consume energía y genera inconsistencia. El trabajo aquí es identificar las 5-10 decisiones que más se repiten en tu operación y construir un criterio claro para cada una.',
  4: 'Tu negocio probablemente tiene mucha información, pero no necesariamente claridad sobre qué números importan. Sin métricas claras, las decisiones se toman por sensación. El trabajo aquí es identificar los 3-5 indicadores que reflejan la salud real del negocio y construir disciplina para revisarlos.',
  5: 'Las reuniones de tu negocio probablemente consumen más de lo que producen. Sin agenda clara, decisiones documentadas y próximos pasos asignados, son tiempo gastado sin cierre. El trabajo aquí es definir cadencia y formato de las reuniones críticas — con qué frecuencia, con quién, con qué estructura.',
  6: 'Cuando intentas delegar, terminas retomando. Eso suele ser síntoma de que las piezas previas (procesos, criterios, roles) no están suficientemente claras. Antes de intentar delegar más, necesitas asegurarte de que los procesos y criterios para lo que vas a delegar están definidos. Solo así la delegación deja de ser un acto de fe.',
  7: 'El negocio depende fuertemente de tu presencia. Esto es la consecuencia acumulada de todo lo demás — procesos, criterios, roles. La buena noticia: este punto no se resuelve directamente, sino al construir las piezas previas. Cuando esos cimientos existen, la independencia del dueño aparece como consecuencia, no como esfuerzo.',
  8: 'Tu estructura puede estar bien construida, pero no se actualiza con el negocio. Los procesos que funcionaron hace 12 meses pueden estar empezando a generar fricción y nadie los está revisando. El trabajo aquí es construir un mecanismo periódico — trimestral, semestral — para auditar qué piezas de la operación necesitan ajustes.',
}
